<template>
  <div class="order-details-grid">
    <download-json-to-csv
      class="order-details-grid__download-json"
      :data="orderDetailsForDownload"
      :file-name="getFilename"
    >
      <button class="order-details-grid__btn">
        Download CSV
      </button>
    </download-json-to-csv>
    <table class="order-details-grid__table">
      <thead>
        <tr class="order-details-grid__table-row order-details-grid__table-row--header">
          <th>{{ getContentItem("Line") }}</th>
          <th>{{ getContentItem("PartNumber") }}</th>
          <th>{{ getContentItem("description") }}</th>
          <th v-if="details.PartsStoreCustomerNumber">{{ getContentItem("partsStoreOrderHashSign") }}</th>
          <template v-if="details.isMcfOrder">
            <th>{{ getContentItem("status") }}</th>
            <th>{{ getContentItem("Warehouse") }}</th>
            <th>{{ getContentItem("Location") }}</th>
          </template>
          <th>{{ getContentItem("PromisedDate") }}</th>
          <th>{{ getContentItem("Ordered") }}</th>
          <th>{{ getContentItem(details.isMcfOrder ? "released" : "Shipped") }}</th>
          <th>{{ getContentItem("QuantityBackordered") }}</th>
          <template v-if="details.showPrices">
            <th>{{ getContentItem("UnitPrice") }}</th>
            <th>{{ getContentItem("ExtPrice") }}</th>
          </template>
        </tr>
      </thead>
      <tbody>
        <template v-for="(item, index) in details.Items">
          <tr
            :key="index"
            class="order-details-grid__table-row order-details-grid__table-row--body"
            :class="{
              'order-details-grid__table-row--sub-line': item.LineType === 'Part',
              'order-details-grid__table-row--muted': item.LineType === 'Comment'
            }"
          >
            <template v-if="item.LineType === 'Part'">
              <td>{{ item.LineNumber }}</td>
              <td>
                <part-details-link :oem-part-code="item.OemPartCode" />
              </td>
              <td>{{ item.Description }}</td>
              <td v-if="details.PartsStoreCustomerNumber">{{ item.PartStoreOrderNumber }}</td>
              <template v-if="details.isMcfOrder">
                <td>{{ getLineStatus(item.LineStatus) }}</td>
                <td>{{ item.SourceWarehouseCode }}</td>
                <td>{{ item.StockingLocation }}</td>
              </template>
              <td>{{ formatDateTime(item.PromisedShipDate) }}</td>
              <td>{{ item.QuantityOrdered }}</td>
              <td>{{ item.QuantityReleased }}</td>
              <td>{{ item.QuantityBackordered }}</td>
              <template v-if="details.showPrices">
                <td>
                  <currency-text
                    :amount="item.UnitPrice"
                    :currency-code="details.CurrencyCode"
                  />
                </td>
                <td>
                  <currency-text
                    :amount="item.ExtendedPrice"
                    :currency-code="details.CurrencyCode"
                  />
                </td>
              </template>
            </template>
            <template v-if="item.LineType === 'Comment'">
              <td class="order-details-grid__table-cell order-details-grid__table-cell--muted">
                {{ item.LineNumber }}
              </td>
              <td class="order-details-grid__table-cell order-details-grid__table-cell--muted">
                <em>Comment</em>
              </td>
              <td colspan="100%" class="order-details-grid__table-cell order-details-grid__table-cell--muted">
                {{ item.Description }} {{ item.Message }}
              </td>
            </template>
          </tr>
        </template>
      </tbody>
      <tfoot v-if="details.showPrices">
        <template v-if="details.TotalCorePrice">
          <tr class="order-details-grid__table-row order-details-grid__table-row--fee">
            <td colspan="100%">
              <b-button v-b-toggle.core-items variant="link">
                Details
              </b-button>
              <label for="corecost" class="order-details-grid__inline-label">Core Costs:</label>
              <currency-text
                id="corecost"
                :amount="details.TotalCorePrice"
                :currency-code="details.CurrencyCode"
              />
              <b-collapse id="core-items">
                <div v-for="(item, index) in details.CoreItems" :key="index" class="order-details-grid__table-row-sub-fee">
                  <label :for="'CorePrice' + index" class="order-details-grid__inline-label">{{ item.OemPartCode }}</label>
                  <currency-text
                    :id="'CorePrice' + index"
                    :amount="item.CorePrice"
                    :currency-code="details.CurrencyCode"
                  />
                </div>
              </b-collapse>
            </td>
          </tr>
        </template>
        <template v-if="details.TotalEmergencyFee">
          <tr class="order-details-grid__table-row order-details-grid__table-row--fee">
            <td colspan="100%">
              <b-button v-b-toggle.emergency-fee-items variant="link">
                Details
              </b-button>
              <label for="corecost" class="order-details-grid__inline-label">Emergency Fees:</label>
              <currency-text
                id="corecost"
                :amount="details.TotalEmergencyFee"
                :currency-code="details.CurrencyCode"
              />
              <b-collapse id="emergency-fee-items">
                <div
                  v-for="(item, index) in details.EmergencyFeeItems"
                  :key="index"
                  class="order-details-grid__table-row-sub-fee"
                >
                  <label :for="'EmergencyFee' + index" class="order-details-grid__inline-label">{{ item.OemPartCode }}</label>
                  <currency-text
                    :id="'EmergencyFee' + index"
                    :amount="item.EmergencyFee"
                    :currency-code="details.CurrencyCode"
                  />
                </div>
              </b-collapse>
            </td>
          </tr>
        </template>
        <template v-if="details.TotalFreight">
          <tr class="order-details-grid__table-row order-details-grid__table-row--fee">
            <td colspan="100%">
              <b-button v-b-toggle.freight-items variant="link">
                Details
              </b-button>
              <label for="corecost" class="order-details-grid__inline-label">Freight Charges:</label>
              <currency-text
                id="corecost"
                :amount="details.TotalFreight"
                :currency-code="details.CurrencyCode"
              />
              <b-collapse id="freight-items">
                <div v-for="(item, index) in details.FreightItems" :key="index" class="order-details-grid__table-row-sub-fee">
                  <label :for="'Freight' + index" class="order-details-grid__inline-label">{{ item.OemPartCode }}</label>
                  <currency-text
                    :id="'Freight' + index"
                    :amount="item.Freight"
                    :currency-code="details.CurrencyCode"
                  />
                </div>
              </b-collapse>
            </td>
          </tr>
        </template>
        <template v-if="details.TotalInsurance">
          <tr class="order-details-grid__table-row order-details-grid__table-row--fee">
            <td colspan="100%">
              <b-button v-b-toggle.insurance-items variant="link">
                Details
              </b-button>
              <label for="corecost" class="order-details-grid__inline-label">Insurance Charges:</label>
              <currency-text
                id="corecost"
                :amount="details.TotalInsurance"
                :currency-code="details.CurrencyCode"
              />
              <b-collapse id="insurance-items">
                <div
                  v-for="(item, index) in details.InsuranceItems"
                  :key="index"
                  class="order-details-grid__table-row-sub-fee"
                >
                  <label :for="'Insurance' + index" class="order-details-grid__inline-label">{{ item.OemPartCode }}</label>
                  <currency-text
                    :id="'Insurance' + index"
                    :amount="item.Insurance"
                    :currency-code="details.CurrencyCode"
                  />
                </div>
              </b-collapse>
            </td>
          </tr>
        </template>
        <template v-if="details.TotalOtherFees">
          <tr class="order-details-grid__table-row order-details-grid__table-row--fee">
            <td colspan="100%">
              <b-button v-b-toggle.other-fee-items variant="link">
                Details
              </b-button>
              <label for="corecost" class="order-details-grid__inline-label">Other Fees:</label>
              <currency-text
                id="corecost"
                :amount="details.TotalOtherFees"
                :currency-code="details.CurrencyCode"
              />
              <b-collapse id="other-fee-items">
                <div
                  v-for="(item, index) in details.OtherFeeItems"
                  :key="index"
                  class="order-details-grid__table-row-sub-fee"
                >
                  <label :for="'OtherFees' + index" class="order-details-grid__inline-label">{{ item.OemPartCode }}</label>
                  <currency-text
                    :id="'OtherFees' + index"
                    :amount="item.OtherFees"
                    :currency-code="details.CurrencyCode"
                  />
                </div>
              </b-collapse>
            </td>
          </tr>
        </template>
        <tr v-show="details.showSubtotal" class="order-details-grid__table-row order-details-grid__table-row--sub-total">
          <td colspan="100%">
            <label for="subtotal" class="order-details-grid__inline-label">{{ getContentItem("subtotal") }}:</label>
            <currency-text
              id="subtotal"
              :amount="details.SubTotal"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { CurrencyText, PartDetailsLink } from "@/components/Elements";
import { formatDateTime } from "@/components/mixins";
import { DownloadJsonToCsv } from "@/components/Elements/DownloadJsonToCsv";

export default {
  name: "OrderDetailsGrid",
  components: {
    CurrencyText,
    PartDetailsLink,
    DownloadJsonToCsv
  },
  mixins: [formatDateTime],
  computed: {
    ...mapGetters(["getContentItem", "hasPermission"]),
    getFilename() {
      var dateString = this.getDateString();
      var orderInvoiceNumber = (typeof this.details.Id != 'undefined' ? this.details.Id : (typeof this.details.OrderNumber != 'undefined') ? this.details.OrderNumber : "");
      return `Order_` + orderInvoiceNumber + `_${dateString}.csv`;
    },
    orderDetailsForDownload() {
      var self = this;
      var orderDetailsForDownload = this.details.Items.map(function(x) {
        return {
          LineNumber: x.LineNumber,
          OemPartCode: x.OemPartCode,
          Description: x.Description,
          PartStoreOrderNumber: x.PartStoreOrderNumber,
          Status: x.LineStatus,
          Warehouse: x.SourceWarehouseCode,
          Location: x.StockingLocation,
          PromisedDate: x.PromisedShipDate,
          Ordered: x.QuantityOrdered,
          Released: x.QuantityReleased,
          QuantityBackordered: x.QuantityBackordered,
          UnitPrice: !self.hasPermission("HideInvoicePricing") ? x.UnitPrice : "",
          ExtendedPrice: !self.hasPermission("HideInvoicePricing") ? x.ExtendedPrice : ""
        }
      });

      return orderDetailsForDownload;
    }
  },
  props: {
    details: Object
  },
  methods: {
    getLineStatus(status) {
      if (status === "NONE") {
        return "";
      }
      return status;
    },
    getDateString() {
      const date = new Date();
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, '0');
      const hour = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      const day = `${date.getDate()}`.padStart(2, '0');
      return `${year}${month}${day}_${hour}${minutes}${seconds}`
    },
  }
};
</script>

<style lang="scss" scoped>
.order-details-grid {
  width: 100%;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  padding: 16px;
  margin: 1rem 0rem;
}

.order-details-grid__download-json {
  margin-bottom: 1em;
}

.order-details-grid__btn {
  background: #1976d2;
  color: #fff;
  border-radius: 6px;
  padding: 6px 16px;
  font-size: 0.95em;
  border: none;
  transition: background 0.2s;
  cursor: pointer;
}
.order-details-grid__btn:hover {
  background: #174ea6;
}

.order-details-grid__table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.order-details-grid__table th,
.order-details-grid__table td {
  border-bottom: 1px solid #e3eaf3;
  padding: 10px 10px;
  text-align: left;
  vertical-align: middle;
  transition: background 0.2s;
}

.order-details-grid__table-row--header th {
  background: #f4f6fb;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e3eaf3;
  letter-spacing: 0.02em;
}

.order-details-grid__table-row--body {
  transition: background 0.2s;
}
.order-details-grid__table-row--body:hover {
  background: #eaf1fb;
}

.order-details-grid__table-row--sub-line {
  background: #f8fafc;
}

.order-details-grid__table-row--muted,
.order-details-grid__table-cell--muted {
  color: #888;
  font-style: italic;
}

.order-details-grid__table-row--fee {
  background: #f4f6fb;
  font-weight: 500;
}
.order-details-grid__table-row--fee td {
  border-bottom: none;
}

.order-details-grid__table-row--sub-total {
  background: #eaf1fb;
  font-weight: bold;
}
.order-details-grid__table-row--sub-total td {
  border-top: 2px solid #e3eaf3;
  text-align: right;
}

.order-details-grid__table-row-sub-fee {
  padding-left: 2em;
  font-size: 0.92em;
  color: #4a5a6a;
}

.order-details-grid__inline-label {
  margin-right: 0.5em;
  color: #333;
  font-size: 0.95em;
  font-weight: 500;
}
</style>